import React, { useState } from 'react'

import styled, { createGlobalStyle } from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import * as editor from '@owl-nest/editor'
import * as api from '@owl-nest/api-client/next'
import { t, tp } from '@owl-nest/localize'
import { imageUploader } from '@owl-nest/behaviours'

type StandardWysiwygProps = {
  value: string
  onChange?: editor.CKEditorConfig['onChange']
  onBlur?: editor.CKEditorConfig['onBlur']
  name: string
  maxLength?: number
  disabled?: boolean
  error?: React.ReactNode
  project: api.Project
  currentFormLang: string
  height?: string
  displayWordCount?: boolean
  wordCountConfig?: {
    onUpdate?: (stats: { words: number; characters: number }) => void
  }
}

export function StandardWysiwyg({
  value,
  onChange,
  name,
  maxLength = 2000,
  disabled = false,
  onBlur,
  error,
  project,
  currentFormLang,
  height = '380px',
  displayWordCount = false,
  wordCountConfig,
}: StandardWysiwygProps) {
  const [uploadImageCommands, setUploadImageCommands] = useState<editor.UploadImageCommands>()
  const [insertMediaCommands, setInsertMediaCommands] = useState<editor.InsertMediaCommands>()

  return (
    <>
      <CKEditorGlobalStyle />
      <plume.WysiwygContent>
        <StyledWysiwyg
          data={value}
          height={height}
          onChange={onChange}
          name={name}
          maxLength={maxLength}
          disabled={disabled}
          error={error}
          onBlur={onBlur}
          initialConfig={{
            language: currentFormLang,
            alignment: {
              options: ['left', 'center', 'right', 'justify'],
            },
            image: {
              toolbar: ['linkImage', 'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText'],
              insert: {
                integrations: ['ulule', 'url'],
              },
            },
            insertMedia: {
              setCommands: setInsertMediaCommands,
            },
            link: {
              defaultProtocol: 'https://',
              decorators: {
                isExternal: {
                  mode: 'automatic',
                  callback: (url) => url !== null && (url.startsWith('https://') || url.startsWith('http://')),
                  attributes: {
                    rel: 'nofollow noopener noreferrer',
                  },
                },
                openInNewTab: {
                  mode: 'manual',
                  label: 'Open in a new tab',
                  attributes: {
                    target: '_blank',
                    rel: 'nofollow noopener noreferrer',
                  },
                },
              },
            },
            plugins: [
              editor.PastFromOffice,
              editor.SourceEditing,
              editor.Font,
              editor.Bold,
              editor.Italic,
              editor.Strikethrough,
              editor.Underline,
              editor.RemoveFormat,
              editor.Essentials,
              editor.Link,
              editor.LinkImage,
              editor.List,
              editor.Paragraph,
              editor.Heading,
              editor.Alignment,
              editor.UluleImageInsert,
              editor.UluleMediaInsert,
              editor.ImageStyle,
              editor.ImageToolbar,
              editor.ImageResize,
              editor.GeneralHtmlSupport,
              ...(displayWordCount ? [editor.WordCount] : []),
            ],
            ...(displayWordCount && {
              wordCount: {
                onUpdate: wordCountConfig?.onUpdate,
              },
            }),
            toolbar: {
              items: [
                'undo',
                'redo',
                '|',
                'link',
                '|',
                'alignment',
                'fontColor',
                'fontBackgroundColor',
                '|',
                'imageInsert',
                'ululeInsertMedia',
                '|',
                'sourceEditing',
                '|',
                '-',
                'bold',
                'italic',
                'underline',
                'strikethrough',
                'removeFormat',
                '|',
                'numberedList',
                'bulletedList',
              ],
              shouldNotGroupWhenFull: true,
            },
            ui: {
              viewportOffset: { top: Number.parseInt(plume.SIZES.HEADER_HEIGHT, 10) },
            },
            htmlSupport: {
              allow: [
                {
                  name: editor.constant.allowedHtmlRegex,
                  styles: editor.constant.allowedStylesRegex,
                },
              ],
            },
            uploadImage: {
              setCommands: setUploadImageCommands,
              upload: async (file, options) => {
                const result = await api.client('POST /projects/:id/images', {
                  urlParams: {
                    id: String(project.id),
                  },
                  body: { image: file, lang: currentFormLang, type: api.ImageType.SECONDARY },
                  signal: options.abortSignal,
                })

                return result
                  .next((success) => {
                    return {
                      default: success.body.versions?.['full'].url || success.body.url || '',
                    }
                  })
                  .doThrow()
              },
            },
          }}
        />
      </plume.WysiwygContent>
      <StyledUploadImageModal
        features={{
          list: {
            insertImage: (source) => {
              if (uploadImageCommands) {
                uploadImageCommands.insertImage({ source })
              }
              setUploadImageCommands(undefined)
            },
            list: async (pagination) => {
              const getParams = new URLSearchParams(pagination?.next ?? '')
              getParams.set('type', 'secondary')
              const result = await api.client('GET /projects/:id/images', {
                urlParams: {
                  id: String(project.id),
                },
                getParams,
              })
              return result
                .next((success) => ({
                  items: (success.body.images ?? [])
                    .map((image) => ({
                      id: image.id,
                      name: image.name,
                      preview: image.versions?.small.url ?? image.url ?? '',
                      src: image.versions?.full.url ?? image.url ?? '',
                      isDeletable: image.is_deletable ?? false,
                    }))
                    .filter((item) => item.src !== ''),
                  next: success.body.meta.next,
                  size: success.body.meta.total_count,
                }))
                .doThrow()
            },
            translations: {
              chooseFromExisting: t('Choose from previous images'),
              loadMore: t('Load more'),
            },
          },
          upload: {
            uploadImage: async (file) => {
              if (uploadImageCommands) {
                await uploadImageCommands.uploadImage({ file })
              }
              setUploadImageCommands(undefined)
            },
            translations: {
              downloadImage: t('Download image'),
              fileFormats: `JPG, GIF ${t('or')} PNG`,
              fileSizeLimit: tp('Max %(size)s', 'Image uploader', {
                size: t('%(fileSize)s %(unit)s', {
                  fileSize: imageUploader.MAX_FILE_SIZE / 1e6,
                  unit: t('MB'),
                }),
              }),
              removalDisabledWarning: '',
              removeImage: t('Remove image'),
              slideItHere: tp('or drop one here', 'Image uploader'),
            },
          },
        }}
        open={uploadImageCommands !== undefined}
        onDismiss={() => setUploadImageCommands(undefined)}
        translations={{
          submitButton: t('Add image'),
          serverSideError: t('Oops... An error occurred during the processing of your image file. Please retry'),
        }}
      />
      <StyledInsertMediaModal
        features={{ embed: {} }}
        initialData={insertMediaCommands?.initialData}
        onConfirm={(data) => {
          if (insertMediaCommands !== undefined) {
            insertMediaCommands.insertMedia(data)
          }
          setInsertMediaCommands(undefined)
        }}
        onDismiss={() => setInsertMediaCommands(undefined)}
        open={insertMediaCommands !== undefined}
        translations={{
          fields: {
            url: {
              label: t('Media URL address'),
              placeholder: t('Youtube, Vimeo or Dailymotion'),
            },
            ratio: {
              label: `${t('Media size')} (%)`,
              placeholder: '',
            },
          },
          errors: {
            badRequest: t('No embed code found, or site is not supported!'),
            internal: t('Oops... An internal error has occured. Please, retry.'),
          },
          title: t('Add media'),
          submitButton: t('Add media'),
        }}
      />
    </>
  )
}

const CKEditorGlobalStyle = createGlobalStyle`
  :root {
    && {
      --ck-z-modal: 1000 !important;
    }
  }
`

const StyledUploadImageModal = styled(editor.UploadImageModal)`
  position: absolute;
  inset: -58px -32px -50px;
`

const StyledInsertMediaModal = styled(editor.InsertMediaModal)`
  position: absolute;
  inset: -58px -32px -50px;
`

const StyledWysiwyg = styled(editor.Wysiwyg)<{ height: string }>`
  .ck-editor__editable_inline {
    height: ${({ height }) => height};
    overflow-y: auto;
    min-height: ${({ height }) => height};
    max-height: ${({ height }) => height};
  }
`
